/*----------------------------------
- CATALOG (SSOT): HOME FEATURE CARDS
----------------------------------*/

/**
 * The five cards under the funnel ("...so you can buy the right one."), one per demo tab and in the tabs' order
 * (docs/mockups/website-home-v3.html, `section#workflow`): the v3 title, its lead and the shorter sentence of
 * 2026-11-04, the arrow link to the page that explains the feature, the tint, the station module drawn at the top of the
 * text, and the product screen that runs off the card.
 *
 * The modules are the mock's renders (docs/mockups/website-home-v3/feature-modules/, 440px tall PNGs), resized to 300px
 * tall (2x the 150px the card draws) and re-encoded to WebP with their transparency in a Chromium canvas, 20 to 33 KB
 * each. "06:00 UTC" in the trends card is the edition's publication hour, a fact of the product, not a served figure.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule Every link is a published website page (the four feature explanations, the daily edition, the sales page);
 *       the "Spot trends" card opens `/trending-domains` (ADR-0074 retired its feature page).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { FeatureCardModule, FeatureCardTint } from '@uniquedomains/components/marketing/feature-cards/FeatureCard';

import AnalysisShot from '@/client/assets/demo/analysis.webp';
import AuctionsShot from '@/client/assets/demo/auctions.webp';
import BrowseShot from '@/client/assets/demo/browse.webp';
import IdeasShot from '@/client/assets/demo/ideas.webp';
import RadarShot from '@/client/assets/demo/radar.webp';
import DroneModule from '@/client/assets/feature-modules/drone-331x300.webp';
import ObservatoryModule from '@/client/assets/feature-modules/observatory-276x300.webp';
import RadarModule from '@/client/assets/feature-modules/radar-350x300.webp';
import RocketModule from '@/client/assets/feature-modules/rocket-170x300.webp';
import TickerTowerModule from '@/client/assets/feature-modules/ticker-tower-267x300.webp';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

/*----------------------------------
- TYPES
----------------------------------*/

/** The demo tab a card echoes; also the card's key and its `data-testid` suffix. */
export type HomeFeatureCardId = 'find' | 'check' | 'watch' | 'trend' | 'market';

export type HomeFeatureCard = {
    id: HomeFeatureCardId;
    tint: FeatureCardTint;
    title: string;
    lead: string;
    body: string;
    link: { label: string; href: string };
    shot: { src: string; alt: string };
    module: FeatureCardModule;
};

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const HOME_FEATURE_CARDS_HEAD = {
    title: '…so you can buy the right one.',
    lead: 'Does this name fit my search? Can I own it? Will I regret it? One place answers all three, so you buy with a reason, not a hunch.',
} as const;

export const HOME_FEATURE_CARDS: readonly HomeFeatureCard[] = [
    {
        id: 'find',
        tint: 'lilac',
        title: 'Find a name',
        lead: 'Every one-word domain that fits your search.',
        body: 'Filter the one-word market the way you decide: ending, price, renewal, sector, word type, demand, brandability and risk. Open names and names for sale, in one table.',
        link: { label: 'See how search works', href: LANDING_WEBSITE_PAGE_ROUTES.productSearch },
        shot: {
            src: BrowseShot,
            alt: 'Browse: filters at the left, the search tabs, the table of names with price, renewal, five-year cost and est. value, and the stats of the search',
        },
        module: { src: ObservatoryModule, width: 276, height: 300 },
    },
    {
        id: 'check',
        tint: 'peach',
        title: 'Check before you buy',
        lead: 'Know why a name is worth buying.',
        body: "One report per name: today's price and the five-year cost, its value against similar names, search demand, history, a trademark check and the cheapest registrar.",
        link: { label: 'See what a report holds', href: LANDING_WEBSITE_PAGE_ROUTES.productVerify },
        shot: { src: AnalysisShot, alt: 'The full analysis of harbor.io' },
        module: { src: RocketModule, width: 170, height: 300 },
    },
    {
        id: 'watch',
        tint: 'blue',
        title: 'Never miss a name',
        lead: 'Save the search. New matches come to you every morning.',
        body: 'A Radar runs your search every day. Each morning, one digest brings the new names that fit and the changes on names you watch: price cuts, auctions, drops.',
        link: { label: 'See how radars work', href: LANDING_WEBSITE_PAGE_ROUTES.productRadars },
        shot: { src: RadarShot, alt: 'A radar page with the names that are new this morning' },
        module: { src: RadarModule, width: 350, height: 300 },
    },
    {
        id: 'trend',
        tint: 'butter',
        title: 'Spot trends',
        lead: 'Rising trends, with the names still open.',
        body: 'Every day at 06:00 UTC, an edition ties rising search trends to one-word names you can buy, with price, renewal and rating, and one business to build on each.',
        link: { label: "See today's edition", href: LANDING_WEBSITE_PAGE_ROUTES.trendingDomains },
        shot: { src: IdeasShot, alt: "Business Ideas: today's rising topics with the open name and a business idea for each" },
        module: { src: DroneModule, width: 331, height: 300 },
    },
    {
        id: 'market',
        tint: 'green',
        title: 'Read the market',
        lead: 'Sales, auctions, drops and prices, matched to your words.',
        body: 'What names like yours sold for, which close at auction today, which drop this week, and what each ending costs at the registrars, matched to your Radars.',
        link: { label: "See today's market", href: LANDING_WEBSITE_PAGE_ROUTES.sales },
        shot: {
            src: AuctionsShot,
            alt: 'Auctions: the names closing today by deadline, the bid against est. value, the venue, and the names on your radar marked.',
        },
        module: { src: TickerTowerModule, width: 267, height: 300 },
    },
];
